<script setup lang="ts">
    import { computed, ref, watch } from 'vue'
    import { useDialog, useSafeLocalStorage } from 'composables'
    import { useWidgetEvents } from '~/utils/emitWidgetEvent'
    import { useChatbotAuth } from '~/composables/useChatbotAuth'
    import { useChatbotStoreSync } from '~/composables/useChatbotStoreSync'
    import { useChatbotProfileProvider } from '~/composables/useChatbotProfileProvider'
    import { useChatbotAgent } from '~/composables/useChatbotAgent'
    import { useChatbotAuthForm } from '~/composables/useChatbotAuthForm'
    import { useFocusTrapBypass } from '~/composables/useFocusTrapBypass'
    import { BACKEND_URL } from '~/utils'
    import {
        initializeAppInsights,
        setTelemetryContext,
        trackAppEvent,
        trackAppException,
    } from '~/modules/applicationInsights'
    import PkChatbot from 'components/src/chat/PkChatbot.vue'
    import PkChatbotAuth from 'components/src/chat/PkChatbotAuth.vue'
    import PkLoader from 'components/src/PkLoader.vue'
    import PkWelcomePopup from 'components/src/chat/PkWelcomePopup.vue'

    defineOptions({
        inheritAttrs: false,
    })

    useFocusTrapBypass()

    const props = withDefaults(
        defineProps<{
            agentId: string
            organizationSlug: string
            baseUrl?: string
            externalToken?: string
            /** Boolean or attribute string ("true"/"false") when set via HTML attribute. */
            awaitExternalToken?: boolean | string
            /**
             * Host-session mode: the widget skips its own sign-in and uses
             * the host platform's better-auth session from the cookie.
             * Boolean or attribute string ("true"/"false").
             */
            useHostSession?: boolean | string
            floatingButtonPosition?: 'left' | 'right'
            customCss?: string
            appinsightsConnectionString?: string
            /** Override the session/anonymous cookie name (defaults to 'eight-wave-ai'). */
            cookieName?: string
            /**
             * Force the chat color scheme instead of following the OS
             * `prefers-color-scheme`. Let a host app hand down its own theme.
             */
            systemTheme?: 'light' | 'dark'
        }>(),
        {
            baseUrl: BACKEND_URL,
            externalToken: undefined,
            awaitExternalToken: false,
            useHostSession: false,
            floatingButtonPosition: undefined,
            customCss: undefined,
            appinsightsConnectionString: undefined,
            cookieName: undefined,
            systemTheme: undefined,
        },
    )

    // Initialize Application Insights (singleton — first call wins)
    initializeAppInsights(props.appinsightsConnectionString)
    setTelemetryContext({
        organizationSlug: props.organizationSlug,
        agentId: props.agentId,
    })

    // dialog
    const { PkGlobalDialog } = useDialog()

    // Widget events (must be called during setup to capture the component instance)
    const { emit: emitWidgetEvent } = useWidgetEvents()

    // chatbot ref
    const chatbotEl = ref<InstanceType<typeof PkChatbot>>()

    // external context set by host application
    const externalContext = ref<Record<string, unknown> | undefined>()

    // open and close chat widget
    const isChatOpen = useSafeLocalStorage(
        computed(() => `${props.agentId}-is-open`),
        false,
    )
    const toggleChatOpen = () => {
        isChatOpen.value = !isChatOpen.value
        trackAppEvent(isChatOpen.value ? 'widget_opened' : 'widget_closed')
        emitWidgetEvent(isChatOpen.value ? 'opened' : 'closed')

        // stop generation when closing the chat while it's generating
        if (!isChatOpen.value) {
            chatbotEl.value?.stopGeneration()
        }
    }

    const {
        session,
        onError: onAuthError,
        headers,
        token,
        userId,
        isExternalAuth,
        signInWithEmail,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        signInWithExternalToken,
        signOut,
    } = useChatbotAuth({
        baseUrl: props.baseUrl,
        externalToken: () => props.externalToken,
        awaitExternalToken:
            !!props.awaitExternalToken && props.awaitExternalToken !== 'false',
        useHostSession: () =>
            !!props.useHostSession && props.useHostSession !== 'false',
        isActive: isChatOpen,
        cookieName: props.cookieName,
        onSignedIn: (detail) => emitWidgetEvent('signed-in', detail),
        onSignedOut: () => emitWidgetEvent('signed-out'),
        onExternalAuthExpired: () => emitWidgetEvent('external-auth-expired'),
    })

    // Enrich telemetry with userId once auth completes
    watch(userId, (id) => {
        if (id) {
            setTelemetryContext({ userId: id })
        }
    })

    // The chat store outlives PkChatbot (v-if'd on isChatOpen): keep the
    // session identity mirrored into it so a login performed while the chat
    // is closed resets the store right away.
    useChatbotStoreSync({
        agentId: props.agentId,
        baseUrl: props.baseUrl,
        headers,
        userId,
    })

    // emit 'ready' once the session is established
    watch(
        () => session.value.data,
        (data) => {
            if (data) {
                emitWidgetEvent('ready')
            }
        },
    )

    function onError(error: { type: string; title: string; status: number }) {
        trackAppException(new Error(`${error.type}: ${error.title}`), {
            status: String(error.status),
        })
        emitWidgetEvent('error', {
            type: error.type,
            title: error.title,
            status: error.status,
        })
        onAuthError(error)
    }

    const {
        agent,
        branding,
        authMethods,
        requiresAuth,
        notFound,
        isLoading,
        contrastColor,
        themeClass,
        themeStyle,
        reload,
    } = useChatbotAgent({
        baseUrl: props.baseUrl,
        organizationSlug: () => props.organizationSlug,
        agentId: () => props.agentId,
        headers,
        systemTheme: () => props.systemTheme,
    })

    // Read-only profile context + voluntary sign-in affordance shared by both
    // widgets. Provides ChatbotProfileContextKey for the chat components.
    const { authBranding } = useChatbotProfileProvider({
        agent,
        branding,
        authMethods,
        session,
        signOut,
        isExternalAuth,
        token,
        baseUrl: props.baseUrl,
        headers,
        cookieName: props.cookieName,
    })

    const effectivePosition = computed(
        () =>
            props.floatingButtonPosition ??
            agent.value?.interface.floatingButtonPosition ??
            branding.value?.interface.floatingButtonPosition ??
            'right',
    )

    const mergedCss = computed(() => {
        const parts = [
            agent.value?.interface?.customCss,
            branding.value?.interface.customCss,
            props.customCss,
        ].filter(Boolean)
        return parts.length ? parts.join('\n') : undefined
    })

    // Login screen (private agents)
    const {
        authError,
        authSuccess,
        authLoading,
        handleSignIn,
        handleSignUp,
        handleReset,
        handleSocial,
    } = useChatbotAuthForm({
        signInWithEmail,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        refetchSession: () => session.value.refetch(),
        reload,
    })

    // Public API exposed on the custom element DOM node
    defineExpose({
        open() {
            if (!isChatOpen.value) {
                toggleChatOpen()
            }
        },
        close() {
            if (isChatOpen.value) {
                toggleChatOpen()
            }
        },
        toggle() {
            toggleChatOpen()
        },
        sendMessage(text: string) {
            chatbotEl.value?.sendMessage(text)
        },
        startNewChat() {
            chatbotEl.value?.startNewChat()
        },
        setContext(context: Record<string, unknown>) {
            externalContext.value = context
        },
        signIn: signInWithExternalToken,
        signOut,
        get isOpen() {
            return isChatOpen.value
        },
    })
</script>

<template>
    <div
        class="floating-chat-widget"
        :class="[
            themeClass,
            {
                'floating-chat-widget--position-left':
                    effectivePosition === 'left',
            },
        ]"
        :style="themeStyle">
        <!-- #region styles -->
        <component :is="'style'" v-if="mergedCss">{{ mergedCss }}</component>
        <!-- #endregion -->

        <!-- #region launcher and welcome popup -->
        <Transition name="slide-bottom">
            <div
                v-if="(agent || branding) && !notFound"
                class="floating-chat-widget__launcher">
                <PkWelcomePopup
                    :agent-id
                    :message="agent?.interface.initialMessage"
                    :enabled="!isChatOpen && agent?.interface?.welcomePopup"
                    @open="toggleChatOpen" />
                <button
                    type="button"
                    aria-label="chat-button"
                    class="floating-chat-widget__button"
                    @click.stop="toggleChatOpen">
                    <svg
                        v-if="isChatOpen"
                        class="w-full h-full"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="currentColor">
                        <path
                            d="M11.9999 13.1714L16.9497 8.22168L18.3639 9.63589L11.9999 15.9999L5.63599 9.63589L7.0502 8.22168L11.9999 13.1714Z"></path>
                    </svg>
                    <svg
                        v-else
                        class="w-full h-full"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="currentColor">
                        <path
                            d="M12 1.99996C12.8632 1.99996 13.701 2.10973 14.5 2.31539L14 4.25192C13.3608 4.0874 12.6906 3.99997 12 3.99997C7.58174 3.99997 4.00002 7.58172 4 12C4 13.3344 4.3255 14.6174 4.93945 15.7656L5.28906 16.4189L4.63379 19.3662L7.58105 18.7109L8.23438 19.0605C9.38255 19.6745 10.6656 20 12 20C16.4183 20 20 16.4183 20 12C20 11.6771 19.9805 11.3587 19.9434 11.0459L21.9297 10.8095C21.976 11.1999 22 11.5972 22 12C22 17.5228 17.5228 22 12 22C10.2975 22 8.69425 21.5746 7.29102 20.8242L2 22L3.17578 16.709C2.42541 15.3057 2 13.7025 2 12C2.00002 6.47714 6.47717 1.99996 12 1.99996ZM19.5293 1.3193C19.7058 0.893513 20.2942 0.8935 20.4707 1.3193L20.7236 1.93063C21.1555 2.97343 21.9615 3.80614 22.9746 4.2568L23.6914 4.57614C24.1022 4.75882 24.1022 5.35635 23.6914 5.53903L22.9326 5.87692C21.945 6.3162 21.1534 7.11943 20.7139 8.1279L20.4668 8.69333C20.2863 9.10747 19.7136 9.10747 19.5332 8.69333L19.2861 8.1279C18.8466 7.11942 18.0551 6.3162 17.0674 5.87692L16.3076 5.53903C15.8974 5.35618 15.8974 4.75895 16.3076 4.57614L17.0254 4.2568C18.0384 3.80614 18.8445 2.97343 19.2764 1.93063L19.5293 1.3193Z"></path>
                    </svg>
                </button>
            </div>
        </Transition>
        <!-- #endregion -->

        <!-- #region chat -->
        <Transition>
            <div
                v-if="
                    isChatOpen &&
                    (requiresAuth || isLoading) &&
                    !(agent && session.data)
                "
                class="floating-chat-widget__chat floating-chat-widget__auth">
                <PkChatbotAuth
                    v-if="requiresAuth"
                    v-model:error-message="authError"
                    v-model:success="authSuccess"
                    :branding
                    :auth-methods
                    :base-url="baseUrl"
                    :loading="authLoading"
                    @sign-in="handleSignIn"
                    @sign-up="handleSignUp"
                    @request-reset="handleReset"
                    @social="handleSocial" />
                <PkLoader v-else />
            </div>
        </Transition>
        <Transition>
            <div
                v-if="isChatOpen && !requiresAuth && !agent && !isLoading"
                class="floating-chat-widget__chat floating-chat-widget__loading">
                <PkLoader />
            </div>
        </Transition>
        <Transition>
            <PkChatbot
                v-if="agent && isChatOpen && session.data"
                :key="agentId"
                ref="chatbotEl"
                class="floating-chat-widget__chat"
                :base-url
                :agent-id
                :user-id
                :name="agent.name"
                :agent-interface="agent.interface"
                :agent-file-upload="agent.capabilities?.fileUpload"
                :voice="agent.capabilities?.voice"
                :headers
                :system-theme="systemTheme"
                :actions="['upvote', 'downvote', 'feedback', 'regenerate']"
                :external-context="externalContext"
                @error="onError">
                <template #actions="{ useColorsForAgentHeader }">
                    <VvButton
                        :modifiers="
                            useColorsForAgentHeader
                                ? `action-quiet-${contrastColor}`
                                : 'action-quiet'
                        "
                        icon="ri:close-line"
                        :title="$t('action.close')"
                        @click.stop="toggleChatOpen" />
                </template>
                <!-- Voluntary sign-in view for anonymous users (chat dropdown) -->
                <template #view-auth>
                    <PkChatbotAuth
                        v-model:error-message="authError"
                        v-model:success="authSuccess"
                        :branding="authBranding"
                        :auth-methods
                        :loading="authLoading"
                        @sign-in="handleSignIn"
                        @sign-up="handleSignUp"
                        @request-reset="handleReset"
                        @social="handleSocial" />
                </template>
            </PkChatbot>
        </Transition>
        <!-- #endregion -->

        <!-- #region global dialogs -->
        <PkGlobalDialog />
        <!-- #endregion -->
    </div>
</template>

<style lang="scss">
    @use '~/assets/scss/style';

    .floating-chat-widget {
        display: contents;

        &__launcher {
            position: fixed;
            bottom: var(--spacing-16);
            right: var(--spacing-16);
            width: fit-content;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: var(--spacing-14);
            background: transparent;
            z-index: 99999997; // under iubenda banner but above everything else
        }

        &__button {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--spacing-10);
            border: 0px;
            width: var(--spacing-48);
            height: var(--spacing-48);
            border-radius: var(--rounded-full);
            background-color: var(
                --chatbot-main-color,
                var(--color-black, #000)
            );
            color: var(--chatbot-contrast-color, var(--color-white, #fff));
            box-shadow: rgb(0 0 0 / 20%) 0px 4px 8px 0px;
            cursor: pointer;
            pointer-events: auto;
            transition: 0.2s ease-in-out;
            display: block;
            transform: scale(1);
            flex-shrink: 0;

            &:hover {
                transform: scale(1.1);
            }
        }

        &__chat {
            position: fixed;
            inset: 0;
            height: auto;
            overflow: hidden;
            z-index: 2147483647; // max z-index to ensure it's always on top

            @include media-breakpoint-up('sm', $breakpoints) {
                inset: auto 16px 78px auto;
                border-radius: var(--rounded-xxl);
                width: 406px;
                height: 85dvh;
                box-shadow:
                    0px 260px 73px 0px rgb(0 0 0 / 0%),
                    0px 166px 67px 0px rgb(0 0 0 / 1%),
                    0px 94px 56px 0px rgb(0 0 0 / 2%),
                    0px 42px 42px 0px rgb(0 0 0 / 3%),
                    0px 10px 23px 0px rgb(0 0 0 / 4%);
                backdrop-filter: blur(4px);
            }
        }

        &__loading {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--color-surface, #fff);
        }

        &__auth {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--color-surface, #fff);
        }

        &--position-left {
            .floating-chat-widget__launcher {
                right: unset;
                left: var(--spacing-16);
                align-items: flex-start;
            }

            .floating-chat-widget__chat {
                @include media-breakpoint-up('sm', $breakpoints) {
                    right: unset;
                    left: 16px;
                }
            }
        }
    }
</style>
